/*
 * <heritage-wire-modal>
 *
 * Read-only "MDJ Wire Information" modal. Opened by any
 * [data-wire-modal-trigger] click or by dispatching the
 * `mdj:wire-open` event on document.
 *
 * Single view: displays account + bank details rendered from theme
 * settings (settings.wire_*). One-tap copy for routing + account
 * numbers via the native clipboard API.
 *
 * Same scaffold as heritage-memo-modal / heritage-reserve-modal: the
 * element portals to document.body on first connect so its fixed
 * overlay escapes any ancestor stacking context (e.g. .shopify-section
 * z-index: 2, sticky header z-index: 100).
 */

class HeritageWireModal extends HTMLElement {
  constructor() {
    super();
    this.handleClose = this.handleClose.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleWireOpen = this.handleWireOpen.bind(this);
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleCopyClick = this.handleCopyClick.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;

    if (document.body && this.parentNode !== document.body) {
      document.body.appendChild(this);
      return;
    }

    this._initialized = true;

    this.overlayEl = this.querySelector('[data-wire-modal-overlay]');
    this.closeEls = this.querySelectorAll('[data-wire-modal-close]');
    this.copyBtns = this.querySelectorAll('[data-wire-copy]');

    this.copyLabel = this.dataset.copyLabel || 'Copy';
    this.copiedLabel = this.dataset.copiedLabel || 'Copied';

    this.closeEls.forEach((btn) => btn.addEventListener('click', this.handleClose));
    if (this.overlayEl) this.overlayEl.addEventListener('click', this.handleClose);
    this.copyBtns.forEach((btn) => btn.addEventListener('click', this.handleCopyClick));

    document.addEventListener('mdj:wire-open', this.handleWireOpen);
    document.addEventListener('click', this.handleTriggerClick);
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('mdj:wire-open', this.handleWireOpen);
    document.removeEventListener('click', this.handleTriggerClick);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleTriggerClick(evt) {
    const trigger = evt.target.closest('[data-wire-modal-trigger]');
    if (!trigger) return;
    evt.preventDefault();
    this.open();
  }

  handleWireOpen() {
    this.open();
  }

  handleClose(evt) {
    if (evt) evt.preventDefault();
    this.close();
  }

  handleKeydown(evt) {
    if (evt.key === 'Escape' && this.dataset.open === 'true') {
      this.close();
    }
  }

  open() {
    this.dataset.open = 'true';
    this.setAttribute('aria-hidden', 'false');
    // Move focus into the modal for keyboard users — Done button is
    // the most predictable landing target (single primary action,
    // unlike the close X which is a corner control).
    const doneBtn = this.querySelector('.heritage-wire-modal__done');
    if (doneBtn) {
      window.setTimeout(() => doneBtn.focus(), 50);
    }
  }

  close() {
    this.dataset.open = 'false';
    this.setAttribute('aria-hidden', 'true');
    // Reset any "Copied" pills back to default so the next open is
    // clean — the timer-based reset already does this 1.5s after a
    // copy, but a user who closes mid-flash should not see stale
    // state on re-open.
    this.copyBtns.forEach((btn) => {
      btn.dataset.copied = 'false';
      btn.textContent = this.copyLabel;
    });
  }

  handleCopyClick(evt) {
    const btn = evt.currentTarget;
    const value = btn.dataset.wireCopy || '';
    if (!value) return;

    this.copyToClipboard(value).then((ok) => {
      if (!ok) return;
      btn.dataset.copied = 'true';
      btn.textContent = this.copiedLabel;
      window.setTimeout(() => {
        btn.dataset.copied = 'false';
        btn.textContent = this.copyLabel;
      }, 1500);
    });
  }

  copyToClipboard(value) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(value).then(() => true).catch(() => false);
    }
    // Fallback for non-secure contexts (rare on Shopify, but safe):
    // hidden textarea + execCommand. Returns synchronously so we wrap
    // in a resolved Promise to keep the call-site uniform.
    try {
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return Promise.resolve(ok);
    } catch (e) {
      return Promise.resolve(false);
    }
  }
}

if (!customElements.get('heritage-wire-modal')) {
  customElements.define('heritage-wire-modal', HeritageWireModal);
}
